// /welcome — onboarding for new users (null username after Google login)
// 3 steps: username, color, how-it-works

import React, { useState, useCallback, useRef } from 'react';
import { POST_COLORS, PostColorHex } from '../types';

interface Props {
  onDone: () => void; // navigate to feed
  onNavigate?: (page: string) => void;
}

type Step = 'username' | 'color' | 'howto';

const STEP_ORDER: Step[] = ['username', 'color', 'howto'];

export function WelcomePage({ onDone, onNavigate }: Props) {
  const [step, setStep] = useState<Step>('username');

  // Step 1 state
  const [username, setUsername] = useState('');
  const [checkState, setCheckState] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [savingUsername, setSavingUsername] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Step 2 state
  const [selectedColor, setSelectedColor] = useState<PostColorHex | null>(null);

  const stepIndex = STEP_ORDER.indexOf(step);

  // ── Step 1: username availability check ──────────────────────────────────────

  const checkUsername = useCallback(async (val: string) => {
    const trimmed = val.trim().toLowerCase();
    if (trimmed.length < 3) {
      setCheckState(trimmed.length === 0 ? 'idle' : 'invalid');
      return;
    }
    if (!/^[a-z0-9_]{3,20}$/.test(trimmed)) {
      setCheckState('invalid');
      return;
    }
    setCheckState('checking');
    try {
      const res = await fetch(`/api/v1/users/check?username=${encodeURIComponent(trimmed)}`);
      const json = await res.json() as { available: boolean };
      setCheckState(json.available ? 'available' : 'taken');
    } catch {
      setCheckState('idle');
    }
  }, []);

  function handleUsernameChange(val: string) {
    const lower = val.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
    setUsername(lower);
    setCheckState('idle');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => checkUsername(lower), 400);
  }

  async function submitUsername() {
    if (checkState !== 'available') return;
    setSavingUsername(true);
    setUsernameError(null);
    try {
      const res = await fetch('/api/v1/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const err = await res.json() as { error: string };
        throw new Error(err.error);
      }
      setStep('color');
    } catch (e) {
      setUsernameError(e instanceof Error ? e.message : 'Failed to save username');
    } finally {
      setSavingUsername(false);
    }
  }

  function finishOnboarding() {
    onDone();
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div style={outerStyle}>
      {/* Progress dots */}
      <div style={dotsRow}>
        {STEP_ORDER.map((s, i) => (
          <div
            key={s}
            style={{
              width: i === stepIndex ? 20 : 6,
              height: 6,
              borderRadius: 3,
              background: i <= stepIndex ? 'var(--accent)' : 'var(--surface2)',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>

      <div style={cardStyle}>
        {/* Legal footer */}
        {onNavigate && (
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
            {(['legal-privacy', 'legal-terms', 'legal-cookies'] as const).map((id, i) => (
              <button
                key={id}
                onClick={() => onNavigate(id)}
                style={{ fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                {['Privacy', 'Terms', 'Cookies'][i]}
              </button>
            ))}
          </div>
        )}

        {step === 'username' && (
          <StepUsername
            username={username}
            checkState={checkState}
            usernameError={usernameError}
            saving={savingUsername}
            onChange={handleUsernameChange}
            onSubmit={submitUsername}
          />
        )}
        {step === 'color' && (
          <StepColor
            selected={selectedColor}
            onSelect={setSelectedColor}
            onNext={() => setStep('howto')}
          />
        )}
        {step === 'howto' && (
          <StepHowTo onDone={finishOnboarding} />
        )}
      </div>
    </div>
  );
}

// ── Step 1: Pick a username ───────────────────────────────────────────────────

function StepUsername({
  username, checkState, usernameError, saving,
  onChange, onSubmit,
}: {
  username: string;
  checkState: 'idle' | 'checking' | 'available' | 'taken' | 'invalid';
  usernameError: string | null;
  saving: boolean;
  onChange: (v: string) => void;
  onSubmit: () => void;
}) {
  const hint =
    checkState === 'checking' ? { text: 'Checking…', color: 'var(--text-muted)' } :
    checkState === 'available' ? { text: '✓ Available', color: '#22c55e' } :
    checkState === 'taken' ? { text: 'Already taken', color: '#ef4444' } :
    checkState === 'invalid' ? { text: '3–20 chars, letters, numbers, underscores only', color: '#ef4444' } :
    null;

  return (
    <>
      <p style={eyebrowStyle}>Welcome to mylodic</p>
      <h1 style={headingStyle}>Choose your username</h1>
      <p style={subStyle}>This is how other people will find you. You can change it later in settings.</p>

      <div style={{ position: 'relative', marginTop: 24 }}>
        <span style={atSignStyle}>@</span>
        <input
          value={username}
          onChange={e => onChange(e.target.value)}
          placeholder="username"
          maxLength={20}
          autoFocus
          style={{ paddingLeft: 30, fontSize: 16 }}
          onKeyDown={e => e.key === 'Enter' && checkState === 'available' && onSubmit()}
        />
      </div>

      {hint && (
        <p style={{ fontSize: 12, marginTop: 6, color: hint.color }}>{hint.text}</p>
      )}
      {usernameError && (
        <p style={{ fontSize: 12, marginTop: 6, color: '#ef4444' }}>{usernameError}</p>
      )}

      <button
        onClick={onSubmit}
        disabled={checkState !== 'available' || saving}
        style={primaryBtnStyle(checkState === 'available' && !saving)}
      >
        {saving ? 'Saving…' : 'Continue'}
      </button>
    </>
  );
}

// ── Step 2: Pick your color ───────────────────────────────────────────────────

function StepColor({
  selected, onSelect, onNext,
}: {
  selected: PostColorHex | null;
  onSelect: (c: PostColorHex) => void;
  onNext: () => void;
}) {
  return (
    <>
      <p style={eyebrowStyle}>Step 2 of 3</p>
      <h1 style={headingStyle}>Pick a color</h1>
      <p style={subStyle}>Choose the color that feels like you right now. It'll tint your posts.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 20 }}>
        {POST_COLORS.map(c => (
          <button
            key={c.hex}
            onClick={() => onSelect(c.hex)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px',
              borderRadius: 'var(--radius)',
              border: selected === c.hex ? `1.5px solid ${c.hex}` : '1.5px solid var(--border)',
              background: selected === c.hex ? `${c.hex}18` : 'var(--surface)',
              cursor: 'pointer',
              transition: 'all 0.15s',
              textAlign: 'left',
            }}
          >
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: c.hex, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.label}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{c.vibe}</div>
            </div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
        <button onClick={onNext} style={ghostBtnStyle}>Skip</button>
        <button onClick={onNext} style={primaryBtnStyle(true)}>Continue</button>
      </div>
    </>
  );
}

// ── Step 3: How it works ──────────────────────────────────────────────────────

function StepHowTo({ onDone }: { onDone: () => void }) {
  const steps = [
    { icon: '🎵', title: 'Find a song', body: 'Search Spotify for the track that matches how you\'re feeling right now.' },
    { icon: '✂️', title: 'Pick a moment', body: 'Clip the exact part of the song that hits different — 10 to 60 seconds.' },
    { icon: '🎙️', title: 'Record a voice note', body: 'Optionally add a voice recording explaining why this moment means something to you.' },
    { icon: '🌊', title: 'Share with the world', body: 'Your moment shows up in the global feed for others to discover and resonate with.' },
  ];

  return (
    <>
      <p style={eyebrowStyle}>Step 3 of 3</p>
      <h1 style={headingStyle}>How mylodic works</h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 24 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 24, lineHeight: 1, flexShrink: 0, width: 36, textAlign: 'center' }}>{s.icon}</div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{s.title}</p>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <button onClick={onDone} style={primaryBtnStyle(true)}>
        Start sharing
      </button>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const outerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
};

const dotsRow: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  marginBottom: 32,
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  display: 'flex',
  flexDirection: 'column',
};

const eyebrowStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--accent)',
  marginBottom: 8,
};

const headingStyle: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 800,
  letterSpacing: '-0.03em',
  lineHeight: 1.15,
  marginBottom: 8,
};

const subStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--text-muted)',
  lineHeight: 1.6,
};

const atSignStyle: React.CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  fontSize: 16,
  pointerEvents: 'none',
};


function primaryBtnStyle(enabled: boolean): React.CSSProperties {
  return {
    marginTop: 20,
    padding: '13px 0',
    borderRadius: 100,
    background: enabled ? 'var(--accent)' : 'var(--surface2)',
    color: enabled ? '#fff' : 'var(--text-muted)',
    fontSize: 15,
    fontWeight: 700,
    width: '100%',
    cursor: enabled ? 'pointer' : 'not-allowed',
    transition: 'background 0.15s',
  };
}

const ghostBtnStyle: React.CSSProperties = {
  marginTop: 20,
  padding: '13px 0',
  borderRadius: 100,
  background: 'transparent',
  color: 'var(--text-muted)',
  fontSize: 15,
  fontWeight: 600,
  flex: '0 0 80px',
  cursor: 'pointer',
};
